import { useEffect, useMemo, useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Route, Switch } from "wouter";
import { navigate } from "wouter/use-browser-location";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ListFilter, Search, Plus, Pencil, Trash2, KeyRound, Download, Upload, ArrowLeft, Loader2 } from "lucide-react";
import CommonGrid from "@/components/pos/admin/common/grid";
import { usePagePersistentState } from "@/hooks/pos/use-page-persisted-state";
import AdvanceFiltersModal, { type FilterConfig } from "@/components/pos/admin/common/advance-filters-modal";
import ActionTemplate from "@/components/pos/admin/common/action-template";
import NoRecordFound from "@/components/pos/admin/common/no-record-found";
import ConfirmModal from "@/components/pos/admin/common/confirmation-modal";
import PasswordConfirmationModal from "@/components/pos/admin/common/password-confirmation-modal";
import { NameAvatar } from "@/components/pos/admin/common/avatar";
import { useDebounce } from "@/hooks/pos/use-debounce";
import { useDateFormatter } from "@/hooks/pos/use-date-formatter";
import { useSortableTable } from "@/hooks/pos/use-sortable-table";
import { usePosAuth } from "@/hooks/pos/use-pos-auth";
import { posApiFetch } from "@/utils/pos-api";
import { getStatusColorCode, getStatusDotColor } from "@/lib/pos/global";
import { useToast } from "@/hooks/use-toast";

type UsersState = {
  role?: string;
  status?: string;
  dateRange?: { from: string; to: string };
  searchQuery?: string;
  currentPage?: number;
  itemsPerPage?: number;
};

const defaultUsersState: UsersState = {
  role: "",
  status: "",
  searchQuery: "",
  currentPage: 1,
  itemsPerPage: 10,
};

const fetchUsers = async () => {
  const res = await posApiFetch("/v1/pos/users");
  if (!res.ok) throw new Error("Failed to fetch users");
  return res.json();
};

const normalizeDate = (value: any): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === "string") {
    if (!value.trim()) return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const getRoleColor = (role: string) => {
  switch (role?.toLowerCase()) {
    case "admin": return "bg-purple-100 text-purple-800 border-purple-200";
    case "manager": return "bg-green-100 text-green-800 border-green-200";
    case "staff": return "bg-orange-100 text-orange-800 border-orange-200";
    default: return "bg-gray-100 text-gray-800 border-gray-200";
  }
};

const getUserStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case "active": return "bg-green-100 text-green-800 border-green-200";
    case "suspended": return "bg-yellow-100 text-yellow-800 border-yellow-200";
    case "inactive": return "bg-red-100 text-red-800 border-red-200";
    default: return "bg-gray-100 text-gray-800 border-gray-200";
  }
};

const getUserStatusDotColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case "active": return "bg-green-800";
    case "suspended": return "bg-yellow-800";
    case "inactive": return "bg-red-800";
    default: return "bg-gray-800";
  }
};

function UsersListPage() {
  const { toast } = useToast();
  const { formatDate } = useDateFormatter();
  const { user } = usePosAuth();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    document.title = "POS Users | SECH Admin";
  }, []);

  const [usersState, setUsersState] = usePagePersistentState<UsersState>("users", defaultUsersState);
  const searchQuery = usersState.searchQuery;
  const currentPage = usersState.currentPage;
  const itemsPerPage = usersState.itemsPerPage;
  const roleFilter = usersState.role;
  const statusFilter = usersState.status;
  const dateRange = usersState.dateRange;
  const debouncedSearch = useDebounce(searchQuery?.trim() ?? "", 300);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [passwordModal, setPasswordModal] = useState(false);
  const [passwordTarget, setPasswordTarget] = useState<any>(null);
  const [importModal, setImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  const { sortColumn, sortDirection, handleSort } = useSortableTable<string>({
    storageKey: "pos_users_table",
    defaultColumn: "createdAt",
    defaultDirection: "desc",
  });

  const roleOptions = [
    { label: "All", value: "all" },
    { label: "Admin", value: "admin" },
    { label: "Manager", value: "manager" },
    { label: "Staff", value: "staff" },
  ];

  const statusOptions = [
    { label: "All", value: "all" },
    { label: "Active", value: "active" },
    { label: "Suspended", value: "suspended" },
    { label: "Inactive", value: "inactive" },
  ];

  const [filterValues, setFilterValues] = useState<Record<string, any>>({
    role: "",
    status: "",
    dateRange: { from: "", to: "" },
  });

  useEffect(() => {
    setFilterValues((prev) => ({
      ...prev,
      role: roleFilter ?? "",
      status: statusFilter ?? "",
      dateRange: { from: dateRange?.from ?? "", to: dateRange?.to ?? "" },
    }));
  }, [roleFilter, statusFilter, dateRange]);

  const handleApplyFilters = (values: Record<string, any>) => {
    const fromDate = normalizeDate(values.dateRange?.from);
    const toDate = normalizeDate(values.dateRange?.to);
    if (fromDate && toDate && fromDate.getTime() > toDate.getTime()) {
      toast({ title: "Invalid filters", description: '"From" date cannot be after "To" date', variant: "destructive" });
      return;
    }
    setFilterValues(values);
    setUsersState((prev) => ({
      ...prev,
      role: values.role,
      status: values.status,
      currentPage: 1,
      dateRange: { from: values.dateRange?.from ?? "", to: values.dateRange?.to ?? "" },
    }));
  };

  const { data: users = [], isLoading, error } = useQuery({
    queryKey: ["/v1/pos/users"],
    queryFn: fetchUsers,
    retry: 3,
    retryDelay: 1000,
  });

  const getFilterCount = () => {
    let count = 0;
    if (usersState.role && usersState.role !== "" && usersState.role !== "all") count += 1;
    if (usersState.status && usersState.status !== "" && usersState.status !== "all") count += 1;
    const fromDate = normalizeDate(usersState.dateRange?.from);
    const toDate = normalizeDate(usersState.dateRange?.to);
    if (fromDate || toDate) count += 1;
    return count;
  };

  const userFilters: FilterConfig[] = useMemo(() => [
    { key: "role", label: "Role", type: "select", options: roleOptions, placeholder: "Select Role" },
    { key: "status", label: "Status", type: "select", options: statusOptions, placeholder: "Select Status" },
    { key: "dateRange", label: "Date Range", type: "dateRange" },
  ], []);

  const handleClear = () => {
    setFilterValues({ role: "", status: "", dateRange: { from: "", to: "" } });
    setUsersState((prev) => ({ ...prev, role: "", status: "", dateRange: { from: "", to: "" }, currentPage: 1 }));
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      const res = await posApiFetch(`/v1/pos/users/${deleteTarget.id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete user");
      toast({ title: "User deleted successfully" });
      queryClient.invalidateQueries({ queryKey: ["/v1/pos/users"] });
    } catch (err: any) {
      toast({ title: "Failed to delete user", description: err?.message || "Unknown error", variant: "destructive" });
    } finally {
      setIsDeleting(false);
      setDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  const handleChangePassword = async (newPassword: string, confirmNewPassword: string) => {
    if (!passwordTarget) return;
    const res = await posApiFetch(`/v1/pos/users/${passwordTarget.id}/password`, {
      method: "PATCH",
      body: JSON.stringify({ newPassword, confirmNewPassword }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data?.message || "Failed to change password");
    }
    toast({ title: "Password changed successfully" });
  };

  const handleExportCsv = async () => {
    try {
      const res = await posApiFetch("/v1/pos/users/export/csv");
      if (!res.ok) throw new Error("Failed to export CSV");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `pos-users-${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast({ title: "CSV exported successfully" });
    } catch (err: any) {
      toast({ title: "Failed to export CSV", description: err?.message || "Unknown error", variant: "destructive" });
    }
  };

  const handleImportCsv = async () => {
    if (!importFile) return;
    setIsImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", importFile);
      const res = await posApiFetch("/v1/pos/users/import/csv", {
        method: "POST",
        headers: {},
        body: formData,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.message || "Failed to import CSV");
      }
      toast({ title: "CSV imported successfully" });
      queryClient.invalidateQueries({ queryKey: ["/v1/pos/users"] });
      setImportModal(false);
      setImportFile(null);
    } catch (err: any) {
      toast({ title: "Failed to import CSV", description: err?.message || "Unknown error", variant: "destructive" });
    } finally {
      setIsImporting(false);
    }
  };

  const tableColumns = useMemo(() => [
    {
      label: "Name", field: "displayName", sortable: true,
      cellTemplate: (_value: string, row: any) => (
        <div className="flex items-center gap-2">
          <NameAvatar name={row.displayName || row.email || ""} imageUrl={row?.imageUrl} />
          <span className="text-[13px] text-[#505258]">{row.displayName || row.email || "-"}</span>
        </div>
      ),
    },
    {
      label: "Email", field: "email", sortable: true,
      cellTemplate: (value: string) => (
        <span className="text-[13px] text-[var(--secondary-text-color)]">{value || "-"}</span>
      ),
    },
    {
      label: "Role", field: "role", sortable: true,
      cellTemplate: (value: string) => (
        <span className={`px-2 h-5 flex items-center w-fit rounded-full text-xs font-medium ${getRoleColor(value)}`}>
          <span className="leading-5 text-[13px] font-normal">{value ? value.charAt(0).toUpperCase() + value.slice(1) : "-"}</span>
        </span>
      ),
    },
    {
      label: "Status", field: "status", sortable: true,
      cellTemplate: (value: string) => (
        <span className={`px-2 h-5 flex items-center w-fit rounded-full text-xs font-medium ${getUserStatusColor(value)}`}>
          <span className={`h-2 w-2 rounded-full mr-1 ${getUserStatusDotColor(value)} inline-flex items-center justify-center`} />
          <span className="leading-5 text-[13px] font-normal">{value ? value.charAt(0).toUpperCase() + value.slice(1) : "-"}</span>
        </span>
      ),
    },
    {
      label: "Last Login", field: "lastLoginAt", sortable: true,
      cellTemplate: (value: string | null) => {
        if (!value) return <span className="text-[13px] text-[var(--secondary-text-color)]">Never</span>;
        return formatDate(value);
      },
    },
    {
      label: "Date & Time", field: "createdAt", sortable: true,
      cellTemplate: (value: string) => formatDate(value),
    },
    {
      label: "Action", field: "id",
      headerTemplate: () => <span className="text-[13px]"><span className="h-[28px] w-[28px] flex items-center justify-center">Action</span></span>,
      cellTemplate: (_value: any, row: any) => (
        <ActionTemplate
          row={row}
          actions={[
            { label: "Edit", icon: Pencil, onClick: (r: any) => navigate(`/admin/pos-users/${r.id}/edit`) },
            { label: "Change Password", icon: KeyRound, onClick: (r: any) => { setPasswordTarget(r); setPasswordModal(true); } },
            { label: "Delete", icon: Trash2, onClick: (r: any) => { setDeleteTarget(r); setDeleteModal(true); }, destructive: true },
          ]}
        />
      ),
    },
  ], []);

  const filteredUsers = useMemo(() =>
    users.filter((u: any) => {
      const search = debouncedSearch.toLowerCase();
      const matchesSearch = !search
        || u.displayName?.toLowerCase().includes(search)
        || u.email?.toLowerCase().includes(search)
        || u.role?.toLowerCase().includes(search)
        || u.status?.toLowerCase().includes(search);
      const matchesRole = !roleFilter || roleFilter === "" || roleFilter === "all" || u.role?.toLowerCase() === roleFilter;
      const matchesStatus = !statusFilter || statusFilter === "" || statusFilter === "all" || u.status?.toLowerCase() === statusFilter;
      const fromDate = normalizeDate(usersState.dateRange?.from);
      const toDate = normalizeDate(usersState.dateRange?.to);
      const createdAt = new Date(u.createdAt).getTime();
      const matchesDateRange = fromDate && toDate ? createdAt >= fromDate.getTime() && createdAt <= toDate.getTime() : true;
      return matchesSearch && matchesRole && matchesStatus && matchesDateRange;
    }), [users, debouncedSearch, roleFilter, statusFilter, usersState.dateRange]
  );

  const sortedUsers = useMemo(() => {
    return [...filteredUsers].sort((a: any, b: any) => {
      let aVal: any, bVal: any;
      switch (sortColumn) {
        case "displayName": aVal = a?.displayName ?? ""; bVal = b?.displayName ?? ""; break;
        case "email": aVal = a?.email ?? ""; bVal = b?.email ?? ""; break;
        case "role": aVal = a?.role ?? ""; bVal = b?.role ?? ""; break;
        case "status": aVal = a?.status ?? ""; bVal = b?.status ?? ""; break;
        case "lastLoginAt": aVal = a?.lastLoginAt ? new Date(a.lastLoginAt).getTime() : 0; bVal = b?.lastLoginAt ? new Date(b.lastLoginAt).getTime() : 0; break;
        case "createdAt": aVal = a?.createdAt ? new Date(a.createdAt).getTime() : 0; bVal = b?.createdAt ? new Date(b.createdAt).getTime() : 0; break;
        default: aVal = a?.createdAt ? new Date(a.createdAt).getTime() : 0; bVal = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      }
      const comparison = typeof aVal === "string" && typeof bVal === "string" ? aVal.localeCompare(bVal) : aVal - bVal;
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [filteredUsers, sortColumn, sortDirection]);

  const totalPages = Math.ceil(sortedUsers.length / (itemsPerPage ?? 10));
  const startIndex = ((currentPage ?? 1) - 1) * (itemsPerPage ?? 10);
  const paginatedUsers = sortedUsers.slice(startIndex, startIndex + (itemsPerPage ?? 10));

  const handlePageChange = (page: number) => { setUsersState((prev) => ({ ...prev, currentPage: page })); };
  const handleItemsPerPageChange = (value: string) => { setUsersState((prev) => ({ ...prev, itemsPerPage: Number(value), currentPage: 1 })); };

  const pagination = { totalPages, currentPage, itemsPerPage, totalItems: sortedUsers.length, handlePageChange, handleItemsPerPageChange };

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md"><CardContent className="p-6">
          <h2 className="text-xl font-bold text-red-600 mb-4">Error Loading Users</h2>
          <p className="text-gray-600 mb-4">Failed to load POS users</p>
          <Button onClick={() => window.location.reload()} className="w-full">Reload Page</Button>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="page-header flex justify-between items-center h-[48px] px-[16px] border-b border-[var(--layout-border-color)]">
        <p className="page-heading text-[20px] leading-[30px] font-semibold">POS Users</p>
        <div className="search-bar-n-btns flex justify-between gap-[4px] items-center">
          <div className={`flex h-8 items-center px-1 p-2 rounded-sm ${getFilterCount() > 0 ? "bg-[var(--secondary-background-color)]" : ""}`}>
            <Button onClick={() => setIsFilterOpen(true)} variant="ghost" className={`px-2 text-[var(--primary-text-color)] flex items-center text-[13px] font-medium leading-[15px] justify-center gap-2 rounded-[4px] ${getFilterCount() > 0 ? "h-6 hover:bg-[var(--primary-background-hover-color)]" : "h-8"}`}>
              <ListFilter className="h-4 w-4" /> Filter
              {getFilterCount() > 0 && <span className="text-[13px] bg-[var(--primary-color)] h-4 w-4 flex items-center justify-center rounded-full leading-[15px] font-medium text-white">{getFilterCount()}</span>}
            </Button>
            {getFilterCount() > 0 && (
              <>
                <span className="text-[13px] text-[var(--secondary-text-color)] px-1">|</span>
                <Button type="button" onClick={handleClear} className="text-[13px] text-[var(--primary-text-color)] font-medium px-2 h-6 hover:bg-[var(--primary-background-hover-color)] bg-transparent">Clear</Button>
              </>
            )}
          </div>
          <div className="search-bar relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#676879] w-4 h-4" />
            <input className="search h-[32px] pr-[12px] pl-[34px] text-[13px] leading-[20px] font-normal rounded-[4px] border border-[#C3C6D4] placeholder:text-[13px] placeholder:leading-[20px] placeholder:font-normal placeholder:text-[#676879] flex items-center" type="text" placeholder="Search" value={searchQuery ?? ""} onChange={(e) => setUsersState((prev) => ({ ...prev, searchQuery: e.target.value, currentPage: 1 }))} />
          </div>
          <Button onClick={handleExportCsv} variant="outline" className="h-[32px] px-3 text-[13px] font-medium rounded-[4px] flex items-center gap-1">
            <Download className="h-4 w-4" /> Export
          </Button>
          <Button onClick={() => setImportModal(true)} variant="outline" className="h-[32px] px-3 text-[13px] font-medium rounded-[4px] flex items-center gap-1">
            <Upload className="h-4 w-4" /> Import
          </Button>
          <Button onClick={() => navigate("/admin/pos-users/add")} className="h-[32px] px-3 text-[13px] font-medium bg-[var(--primary-color)] text-white rounded-[4px] flex items-center gap-1">
            <Plus className="h-4 w-4" /> Add User
          </Button>
        </div>
      </div>

      <div className="p-[16px]">
        <div className="pt-3 h-[calc(100vh-130px)]">
          {!paginatedUsers.length && !isLoading ? (
            <NoRecordFound title="Users" description="There are no POS users available." />
          ) : (
            <CommonGrid
              rowData={paginatedUsers}
              isLoading={isLoading}
              columns={tableColumns}
              pagination={pagination}
              isPaginationRequired={true}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
          )}
        </div>
      </div>

      <AdvanceFiltersModal isOpen={isFilterOpen} onClose={() => setIsFilterOpen(false)} title="Filter" filters={userFilters} initialValues={filterValues} onApply={handleApplyFilters} onClearAll={handleClear} />
      <ConfirmModal open={deleteModal} onClose={() => { setDeleteModal(false); setDeleteTarget(null); }} onConfirm={handleDelete} title="Delete User" description="Are you sure you want to delete this user? This action cannot be undone." confirmText={isDeleting ? "Deleting..." : "Delete"} variant="delete" />
      <PasswordConfirmationModal
        isOpen={passwordModal}
        onClose={() => { setPasswordModal(false); setPasswordTarget(null); }}
        onSave={handleChangePassword}
      />

      <Dialog open={importModal} onOpenChange={(open) => { if (!open) { setImportModal(false); setImportFile(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Users from CSV</DialogTitle>
            <DialogDescription>Select a CSV file to import POS users. The file should contain columns for email, displayName, role, and status.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="csv-upload" className="text-[13px] font-medium">CSV File</Label>
            <Input
              id="csv-upload"
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className="mt-2"
              onChange={(e) => setImportFile(e.target.files?.[0] ?? null)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setImportModal(false); setImportFile(null); }} disabled={isImporting}>Cancel</Button>
            <Button onClick={handleImportCsv} disabled={!importFile || isImporting}>
              {isImporting ? "Importing..." : "Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Shared POS user editor used by both Add and Edit ---

interface UserFormState {
  displayName: string;
  email: string;
  password: string;
  confirmPassword: string;
  role: string;
  status: string;
}

const EMPTY_USER: UserFormState = {
  displayName: "",
  email: "",
  password: "",
  confirmPassword: "",
  role: "staff",
  status: "active",
};

function UserEditor({ userId }: { userId?: string }) {
  const isEdit = !!userId;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<UserFormState>(EMPTY_USER);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    document.title = isEdit ? "Edit POS User | SECH Admin" : "Add POS User | SECH Admin";
  }, [isEdit]);

  const { data: existingUser, isLoading } = useQuery<any>({
    queryKey: ["/v1/pos/users", userId],
    queryFn: async () => {
      const res = await posApiFetch(`/v1/pos/users/${userId}`);
      if (!res.ok) throw new Error("Failed to fetch user");
      return res.json();
    },
    enabled: isEdit,
  });

  useEffect(() => {
    if (!existingUser) return;
    const u = existingUser;
    setForm({
      displayName: u.displayName ?? "",
      email: u.email ?? "",
      password: "",
      confirmPassword: "",
      role: u.role ?? "staff",
      status: u.status ?? "active",
    });
  }, [existingUser]);

  const set = <K extends keyof UserFormState>(key: K, value: UserFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email.trim()) {
      toast({ title: "Validation Error", description: "Email is required", variant: "destructive" });
      return;
    }
    if (!isEdit && !form.password) {
      toast({ title: "Validation Error", description: "Password is required for new users", variant: "destructive" });
      return;
    }
    if (form.password && form.password !== form.confirmPassword) {
      toast({ title: "Validation Error", description: "Passwords do not match", variant: "destructive" });
      return;
    }
    if (form.password && form.password.length < 8) {
      toast({ title: "Validation Error", description: "Password must be at least 8 characters", variant: "destructive" });
      return;
    }

    setIsSaving(true);
    try {
      const body: Record<string, any> = {
        displayName: form.displayName.trim() || null,
        email: form.email.trim(),
        role: form.role,
        status: form.status,
      };
      if (form.password) {
        body.password = form.password;
      }

      const url = isEdit ? `/v1/pos/users/${userId}` : "/v1/pos/users";
      const method = isEdit ? "PUT" : "POST";

      const res = await posApiFetch(url, { method, body: JSON.stringify(body) });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.message || `Failed to ${isEdit ? "update" : "create"} user`);
      }

      toast({ title: `User ${isEdit ? "updated" : "created"} successfully` });
      queryClient.invalidateQueries({ queryKey: ["/v1/pos/users"] });
      navigate("/admin/pos-users");
    } catch (err: any) {
      toast({ title: "Error", description: err?.message || "Failed to save user", variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  if (isEdit && isLoading) {
    return (
      <div className="h-full overflow-y-auto p-4">
        <div className="flex items-center gap-2 mb-4">
          <Button variant="ghost" onClick={() => navigate("/admin/pos-users")} className="text-[13px] gap-1">
            <ArrowLeft className="h-4 w-4" /> Back to Users
          </Button>
        </div>
        <div className="flex items-center justify-center min-h-[300px]">
          <Loader2 className="h-6 w-6 animate-spin text-[var(--secondary-text-color)]" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" onClick={() => navigate("/admin/pos-users")} className="text-[13px] gap-1">
          <ArrowLeft className="h-4 w-4" /> Back to Users
        </Button>
        <h2 className="text-[16px] font-semibold text-[var(--primary-text-color)]">
          {isEdit ? "Edit POS User" : "Add POS User"}
        </h2>
        <div className="w-[140px]" />
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 max-w-[900px] mx-auto pb-8">
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-[14px] font-semibold">Account Details</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-[12px]">Display Name</Label>
                <Input className="h-[36px] text-[13px]" value={form.displayName} onChange={(e) => set("displayName", e.target.value)} placeholder="John Doe" />
              </div>
              <div className="space-y-1">
                <Label className="text-[12px]">Email *</Label>
                <Input className="h-[36px] text-[13px]" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="user@example.com" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-[12px]">Role *</Label>
                <Select value={form.role} onValueChange={(v) => set("role", v)}>
                  <SelectTrigger className="h-[36px] text-[13px]">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[12px]">Status *</Label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger className="h-[36px] text-[13px]">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-[14px] font-semibold">
              {isEdit ? "Change Password (optional)" : "Password *"}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-[12px]">{isEdit ? "New Password" : "Password *"}</Label>
                <Input className="h-[36px] text-[13px]" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder={isEdit ? "Leave blank to keep current" : "Min 8 chars, upper, lower, number, special"} />
              </div>
              <div className="space-y-1">
                <Label className="text-[12px]">Confirm Password</Label>
                <Input className="h-[36px] text-[13px]" type="password" value={form.confirmPassword} onChange={(e) => set("confirmPassword", e.target.value)} placeholder="Re-enter password" />
              </div>
            </div>
            {!isEdit && (
              <p className="text-[11px] text-[var(--secondary-text-color)]">
                Must be at least 8 characters with uppercase, lowercase, number, and special character.
              </p>
            )}
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button type="button" variant="outline" className="h-[36px] text-[13px]" onClick={() => navigate("/admin/pos-users")} disabled={isSaving}>Cancel</Button>
          <Button type="submit" className="h-[36px] text-[13px] bg-[var(--primary-color)] text-white hover:bg-[#9f0e0e]" disabled={isSaving}>
            {isSaving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {isEdit ? "Update User" : "Create User"}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default function PosUsersPage() {
  return (
    <Switch>
      <Route path="/admin/pos-users/add">
        <UserEditor />
      </Route>
      <Route path="/admin/pos-users/:id/edit">
        {(params) => <UserEditor userId={params.id} />}
      </Route>
      <Route path="/admin/pos-users" component={UsersListPage} />
    </Switch>
  );
}
